<script setup>
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.min.css";
import { onMounted,ref } from "vue";
import { useUserStore } from "../../../stores/user";
import { useRoute } from "vue-router/auto";
import { setPageTitle } from "../../../utils/pageTitle";
import OrderHeader from "../../../components/order/OrderHeader.vue";
import { shortDateWithTimeTz } from "../../../utils/dateUtils";

import { AgGridVue } from "ag-grid-vue3";
import { useThemeStore } from "@/stores/themeStore";
import { getDefaultColDefs, getDefaultGridOptions } from "@/utils/aggridCommonConfigs";
import { connectToServerApi } from "@/utils/agGridUtils";
import { LicenseManager } from "ag-grid-enterprise";
import useOutboundOrders from "@/hooks/useOutboundOrders";
import OutboundOrderLayout from "@/layouts/OutboundOrderLayout.vue";

LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);

const { outboundOrder, fetchOutboundOrder } = useOutboundOrders();

const userStore = useUserStore();
const route = useRoute();
const themeStore = useThemeStore();

let gridApi;
let context = ref(null);

const OUTBOUND_ORDERS_LP_ITEM_TRANSACTIONS_URL = `/outbound_orders/${route.params.id}/lp_item_transactions?expand=product`;

const advancedFilterKeys = ["id", "created_at", "lp.number", "lp.lot", "qty", "created_by.name", "lp.expiry"];

onMounted(async () => {
  await fetchOutboundOrder(route.params.id).then(() => {
    setPageTitle(`Outbound Order #OUT-${outboundOrder.value.id}`);
  });
});

const onGridReady = (params) => {
  gridApi = params.api;
  context.value = {
    apiUrl: OUTBOUND_ORDERS_LP_ITEM_TRANSACTIONS_URL,
    advancedFilterKeys: advancedFilterKeys,
  }
  gridApi.setGridOption("serverSideDatasource", connectToServerApi());
};

const defaultColDef = getDefaultColDefs();

const columnDefs = [
  { field: "id", minWidth: 50, headerName: "ID", colId: "id", cellDataType: "text"},
  {
    field: "created_at", minWidth: 220, headerName: "DATE", colId: "created_at", cellDataType: "text", 
    valueGetter: (params) => {
      return shortDateWithTimeTz(params.data.created_at);
    }
  },
  { field: "lp_item_id", minWidth: 50, headerName: "ITEM #", colId: "lp_item_id", cellDataType: "text", width: "300" },
  { field: "lp.product.sku", minWidth: 200, headerName: "SKU", colId: "lp.product.sku", cellDataType: "text", width: "300",
    cellRenderer: function (params) {
      if (params.data.lp.product.sku) {
        return `<a href="/products/${params.data.lp.product_id}">${params.data.lp.product.sku}</a>`;
      } else {
        return '';
      }
    },
  },
  { field: 'qty', minWidth: 120, headerName: "QUANTITY", colId: "qty", cellDataType: "text", width: "300",
    valueGetter: (params) => {
      return params.data.qty + " " + params.data.lp.product.default_unit;
    }
  },
  { field: "lp.number", minWidth: 150, headerName: "LP NUMBER", colId: "lp.number", cellDataType: "text",
    cellRenderer: function (params) {
      if (params.data.lp.number) {
        return `<a href="/lps/${params.data.lp.id}">${params.data.lp.number}</a>`;
      } else {
        return '';
      }
    },
  },
  { field: "lp.bin.name", minWidth: 150, headerName: "LOCATION", colId: "lp.bin.name", width: "300",    
    cellRenderer: function (params) {
      if (params.data.lp.bin.name) {
        return `<a href="/bins/${params.data.lp.bin.id}">${params.data.lp.bin.name}</a>`;
      } else {
        return '';
      }
    },
  },
  { field: "lp.lot", minWidth: 150, headerName: "LOT CODE", colId: "lp.lot", cellDataType: "text", width: "300" },
  { field: "lp.expiry", minWidth: 150, headerName: "EXPIRY", colId: "lp.expiry", cellDataType: "text", width: "300" },
  { field: "created_by.name", minWidth: 150, headerName: "USER", colId: "created_by.name", cellDataType: "text", width: "300",
    cellRenderer: function (params) {
      if (params.data.created_by.name) {
        return `<a href="/users/${params.data.created_by.id}">${params.data.created_by.name}</a>`;
      } else {
        return '';
      }
    },
  },
];

const gridOptions = getDefaultGridOptions(columnDefs);

</script>

<template>
  <OutboundOrderLayout :outboundOrder="outboundOrder" v-if="outboundOrder">
    <div v-if="userStore.user && outboundOrder" class="px-4">
      <div class="w-100 px-2">
        <div class="w-100">
          <OrderHeader
            orderType="outbound"
            :orderId="outboundOrder.id"
            :status="outboundOrder.order_status.short_label"
          />
        </div>

        <v-card class="mt-4 pa-2 w-auto" elevation="0">
          <v-card-title class="text-h5">Pallet Transactions</v-card-title>
        </v-card>

        <div class="outbound-inventory-table">
          <AgGridVue 
            style="width: 100%; height: 100%"
            :class="themeStore.agTheme"
            :columnDefs="columnDefs"
            :defaultColDef="defaultColDef"
            :gridOptions="gridOptions"
            @grid-ready="onGridReady"
            :context="context"
          ></AgGridVue>
        </div>
      </div>
    </div>
  </OutboundOrderLayout>
</template>

<style scoped>
.outbound-inventory-table {
  height: 670px;
  margin-top: 15px;
}
</style>